import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Play } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { tr } from "@/i18n/translations";
import LanguageSwitch from "@/components/LanguageSwitch";

const ToniniBitlab = () => {
  const { lang } = useLanguage();
  const [activeVideo, setActiveVideo] = useState(null);
  return (
    <div
      className="relative min-h-screen w-full overflow-hidden"
      data-testid="tonini-page"
    >
      <LanguageSwitch />
      {activeVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.92)" }}
          onClick={() => setActiveVideo(null)}
        >
          <div className="relative w-full max-w-3xl mx-4" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setActiveVideo(null)}
              className="mb-3 flex items-center gap-2 na-btn na-btn-ghost text-xs"
              style={{ letterSpacing: "0.2em" }}
            >
              ← NAZAD
            </button>
            <div style={{ position: "relative", paddingTop: "56.25%", borderRadius: "6px", overflow: "hidden", border: "1px solid rgba(34,255,119,0.3)", boxShadow: "0 0 40px rgba(34,255,119,0.15)" }}>
              <iframe
                src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1`}
                title="Tonini BitLab Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
              />
            </div>
            <p className="mt-3 text-center text-xs text-zinc-500 uppercase tracking-[0.2em]">Klikni van videa ili pritisni NAZAD da zatvoris</p>
          </div>
        </div>
      )}
      <div className="na-grid" />
      <div className="na-noise" />

      <main className="relative z-10 mx-auto max-w-4xl px-5 py-12 sm:px-8 sm:py-16">
        {/* Back button + logo */}
        <div className="na-anim na-delay-1 flex items-center justify-between">
          <Link to="/" className="na-btn na-btn-ghost" data-testid="back-button">
            <ArrowLeft size={14} />
            {tr("common", "back", lang)}
          </Link>
          <div className="flex items-center gap-4">
            <img
              src="/logo-tonini-bitlab.png"
              alt="Tonini BitLab"
              data-testid="logo-tonini"
              className="h-16 w-auto object-contain"
            />
            <img
              src="/logo-tonini-finance.png"
              alt="Tonini Finance"
              data-testid="logo-tonini-finance"
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>

        {/* Heading */}
        <header className="na-anim na-delay-2 mt-10">
          <span className="na-tag">{tr("tonini", "tag", lang)}</span>
          <h1
            className="na-display mt-5 text-3xl leading-tight sm:text-4xl lg:text-5xl"
            style={{ letterSpacing: "0.12em" }}
            data-testid="tonini-title"
          >
            <span className="text-white">Tonini </span>
            <span className="na-glow-green na-pulse-green">BitLab</span>
          </h1>
          <p className="mt-5 text-base leading-relaxed text-zinc-300 sm:text-lg">
            {tr("tonini", "lead", lang)}
          </p>
        </header>

        {/* VIDEO UVOD baner */}
        <button
          onClick={() => setActiveVideo("0g7CXRTXn0I")}
          className="na-video-banner mt-8 w-full"
        >
          <div className="flex items-center justify-start gap-4">
            <div className="na-play-icon">▶</div>
            <div className="text-left">
              <div className="na-display text-sm text-[var(--na-green)]" style={{ letterSpacing: "0.18em" }}>POGLEDAJTE UVOD — SPAS OD INFLACIJE</div>
              <div className="text-xs text-zinc-400 mt-1">5 min · Zašto vaš novac u banci gubi vrednost i kako Tonini BitLab to menja</div>
            </div>
          </div>
        </button>

        <hr className="na-divider" />

        {/* Intro */}
        <section className="na-anim na-delay-3 na-prose">
          <p>
            <strong>{tr("tonini", "introStrong", lang)}</strong>
            {tr("tonini", "introRest", lang)}
          </p>
        </section>

        {/* Sections */}
        <div className="mt-10 space-y-6">
          <div className="na-section na-anim na-delay-3">
            <h3 className="na-display text-base text-[var(--na-green)]">
              {tr("tonini", "s1Title", lang)}
            </h3>
            <div className="na-prose mt-3">
              <p>{tr("tonini", "s1p1", lang)}</p>
              <p>
                {tr("tonini", "s1p2pre", lang)}
                <strong>{tr("tonini", "s1p2strong", lang)}</strong>.
              </p>
            </div>
          </div>

          <div className="na-section na-anim na-delay-4">
            <h3 className="na-display text-base text-[var(--na-orange)]">
              {tr("tonini", "s2Title", lang)}
            </h3>
            <div className="na-prose mt-3">
              <p>
                {tr("tonini", "s2pre", lang)}
                <a
                  href="/teambit-prevod.pdf"
                  className="na-glow-green underline decoration-dotted underline-offset-4 transition-opacity hover:opacity-80"
                  data-testid="teambit-link"
                >
                  {tr("tonini", "s2teambit", lang)}
                </a>
                {tr("tonini", "s2mid", lang)}
                <span className="na-glow-green">+172 %</span>
                {tr("tonini", "s2post", lang)}
              </p>
            </div>
          </div>

          <div className="na-section na-anim na-delay-4">
            <h3 className="na-display text-base text-[var(--na-green)]">
              {tr("tonini", "s3Title", lang)}
            </h3>
            <div className="na-prose mt-4 space-y-5">
              <div>
                <p>
                  <strong>1. MYNERENT</strong> {tr("tonini", "s3o1", lang)}
                  <strong>{tr("tonini", "s3o1strong", lang)}</strong>
                  {tr("tonini", "s3o1rest", lang)}
                  <em>Sale &amp; Lease Back</em>
                  {tr("tonini", "s3o1contract", lang)}
                </p>
              </div>
              <div>
                <p>
                  <strong>{tr("tonini", "s3o2strong", lang)}</strong>
                  {tr("tonini", "s3o2", lang)}
                </p>
              </div>
              <div>
                <p>
                  <strong>{tr("tonini", "s3o3strong", lang)}</strong>
                  {tr("tonini", "s3o3", lang)}
                  <strong>{tr("tonini", "s3o3years", lang)}</strong>
                  {tr("tonini", "s3o3rest", lang)}
                </p>
              </div>
            </div>
          </div>

          <div className="na-section na-anim na-delay-5">
            <h3 className="na-display text-base text-[var(--na-orange)]">
              {tr("tonini", "s4Title", lang)}
            </h3>
            <div className="na-prose mt-3">
              <p>{tr("tonini", "s4p1", lang)}</p>
              <p className="text-[var(--na-green)]">
                {tr("tonini", "s4p2", lang)}
              </p>
            </div>
          </div>
        </div>

        <hr className="na-divider" />

        {/* 3 video banera */}
        <div className="space-y-4">
          <div className="text-center mb-2">
            <span className="na-tag">// VIDEO MATERIJALI</span>
          </div>
          <button onClick={() => setActiveVideo("0g7CXRTXn0I")} className="na-video-banner w-full">
            <div className="flex items-center justify-start gap-4">
              <div className="na-play-icon">▶</div>
              <div className="text-left">
                <div className="na-display text-sm text-[var(--na-green)]" style={{ letterSpacing: "0.18em" }}>KO JE FRANCESCO TONINI?</div>
                <div className="text-xs text-zinc-400 mt-1">Upoznajte Tonini BitLab i Tonini Finance LTD — industrijsku priču iza tehnologije</div>
              </div>
            </div>
          </button>
          <button onClick={() => setActiveVideo("0g7CXRTXn0I")} className="na-video-banner w-full">
            <div className="flex items-center justify-start gap-4">
              <div className="na-play-icon">▶</div>
              <div className="text-left">
                <div className="na-display text-sm text-[var(--na-green)]" style={{ letterSpacing: "0.18em" }}>PROIZVODI — RENT I MAŠINE</div>
                <div className="text-xs text-zinc-400 mt-1">Rent paketi i fizičke mašine za rudarenje</div>
              </div>
            </div>
          </button>
          <button onClick={() => setActiveVideo("0g7CXRTXn0I")} className="na-video-banner w-full">
            <div className="flex items-center justify-start gap-4">
              <div className="na-play-icon">▶</div>
              <div className="text-left">
                <div className="na-display text-sm text-[var(--na-green)]" style={{ letterSpacing: "0.18em" }}>NETWORK MARKETING — DODATNI PRIHOD</div>
                <div className="text-xs text-zinc-400 mt-1">Kako funkcioniše dodatni prihod kroz izgradnju tima</div>
              </div>
            </div>
          </button>
        </div>

        <hr className="na-divider" />

        {/* CTA dugmad */}
        <div
          className="na-anim na-delay-5 flex flex-col items-stretch justify-center gap-4 sm:flex-row sm:items-center"
          data-testid="tonini-cta"
        >
          <a
            href="https://forms.gle/Fu4UEiWu89egjvWHA"
            className="na-btn na-btn-primary"
            data-testid="tonini-contact-button"
          >
            KONTAKTIRAJTE NAS
          </a>
          <a
            href="https://discord.gg/Z6AXUFpNG9"
            className="na-btn na-btn-outline"
            data-testid="tonini-discord-button"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" /><path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1" /></svg>
            {tr("tonini", "telegramBtn", lang)}
          </a>
        </div>

        {/* Community temelji */}
        <div
          className="na-anim na-delay-5 mt-14 flex flex-col items-center"
          data-testid="tonini-community"
        >
          <div className="mb-6 text-[10px] uppercase tracking-[0.35em] text-zinc-500">
            {tr("common", "poweredBy", lang)}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
            <img
              src="/logo-nasa-com.png"
              alt="NAŠA.com"
              className="h-10 w-auto object-contain opacity-85 transition-opacity duration-300 hover:opacity-100"
            />
            <img
              src="/logo-nasa-community.png"
              alt="NAŠA Community"
              className="h-10 w-auto object-contain opacity-85 transition-opacity duration-300 hover:opacity-100"
            />
          </div>
        </div>

        <footer className="mt-16 border-t border-white/5 pt-6 text-center text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          © {new Date().getFullYear()} Tonini BitLab · Naša Adria
        </footer>
      </main>
    </div>
  );
};

export default ToniniBitlab;
